// Meta Pixel tracking utility
//
// Vorab gerenderte Seiten (Speculation Rules, Paket 1d): fbevents.js kennt
// `document.prerendering` nicht. Events, die beim Mount einer Seite feuern
// (ViewContent auf den Landingpages und im Schnuppertag-Formular, das Custom-Event
// `Schnuppertag_klick`), gingen sonst für eine unsichtbare Seite an Meta. Deshalb
// warten alle Sende-Wege hier auf die Aktivierung. Während eines Prerenders kann
// niemand klicken oder tippen, also trifft die Verzögerung nur Mount-Events; im
// Normalfall läuft alles sofort und synchron wie bisher.
import { bisAktiviert, nachAktivierung } from "@/lib/prerendering";

export const META_PIXEL_ID = '850802317703311';

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}

/**
 * Normalize a phone number for Meta Enhanced Matching:
 * Strip non-digits, replace leading 0 with 49 (Germany)
 */
const normalizePhone = (phone: string): string => {
  let digits = phone.replace(/[^0-9]/g, '');
  if (digits.startsWith('0')) {
    digits = '49' + digits.substring(1);
  }
  return digits;
};

/**
 * Re-initialize Meta Pixel with user data for Enhanced Matching.
 * Call after login, signup, or when user data becomes available.
 * Only non-empty fields are sent. Meta hashes them automatically.
 */
export const updateMetaPixelUserData = (userData: {
  em?: string;
  fn?: string;
  ln?: string;
  ph?: string;
}) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    const matchData: Record<string, string> = {};
    if (userData.em) matchData.em = userData.em.toLowerCase().trim();
    if (userData.fn) matchData.fn = userData.fn.toLowerCase().trim();
    if (userData.ln) matchData.ln = userData.ln.toLowerCase().trim();
    if (userData.ph) {
      const normalized = normalizePhone(userData.ph);
      if (normalized.length >= 8) matchData.ph = normalized;
    }
    // Always set country for German users
    matchData.country = 'de';

    if (Object.keys(matchData).length > 1) { // > 1 because country is always set
      window.fbq('init', META_PIXEL_ID, matchData);
    }
  }
};

/**
 * Track a Meta Pixel event
 * @param event - The event name (e.g., 'Lead', 'CompleteRegistration', 'Schedule')
 * @param params - Optional parameters for the event
 */
export const trackMetaEvent = (event: string, params?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.fbq) {
    nachAktivierung(() => window.fbq('track', event, params));
  }
};

/**
 * Track a custom Meta Pixel event (trackCustom)
 */
export const trackMetaCustomEvent = (event: string, params?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.fbq) {
    nachAktivierung(() => window.fbq('trackCustom', event, params));
  }
};

// Pre-defined event helpers for common conversions
export const trackLead = (params?: { content_name?: string; content_category?: string; value?: number; currency?: string }) => {
  trackMetaEvent('Lead', params);
};

export const trackSchedule = (params?: { content_name?: string }) => {
  trackMetaEvent('Schedule', params);
};

export const trackCompleteRegistration = (params?: { content_name?: string; value?: number; currency?: string }) => {
  trackMetaEvent('CompleteRegistration', params);
};

// Kontaktaufnahme (WhatsApp, Telefon). Seit 15.10.2026 auch für den WhatsApp-Klick im Footer
// und auf /faq, der vorher als `Lead` lief: eine Kampagne, die auf `Lead` optimiert, hätte sonst
// auf WhatsApp-Klicks mitoptimiert (Lückenanalyse HuTa-Ads, Zeile 4a).
export const trackContact = (params?: { content_name?: string }) => {
  trackMetaEvent('Contact', params);
};

export const trackSubscribe = (params?: { value?: number; currency?: string; predicted_ltv?: number }) => {
  trackMetaEvent('Subscribe', params);
};

export const trackInitiateCheckout = (params?: { value?: number; currency?: string; content_name?: string }) => {
  trackMetaEvent('InitiateCheckout', params);
};

export const trackPurchase = (params?: { value?: number; currency?: string; content_name?: string; content_type?: string; ab_variant?: string }) => {
  trackMetaEvent('Purchase', params);
};

export const trackViewContent = (params?: { content_name?: string; content_category?: string; content_type?: string; value?: number; currency?: string }) => {
  trackMetaEvent('ViewContent', params);
};

// ============================================================
// CAPI Pixel-CAPI dual-tracking (Phase 3)
// ============================================================

/**
 * Generate a UUID v4 for event_id (used to dedupe Pixel + CAPI events).
 * Falls back to crypto.randomUUID if available, else timestamp+random.
 */
export const generateEventId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

import { supabase } from "@/integrations/supabase/client";
import { getFacebookTrackingData } from "@/lib/facebookTracking";

interface CAPIUserData {
  em?: string; ph?: string; fn?: string; ln?: string;
  ct?: string; zp?: string; country?: string; external_id?: string;
}

/**
 * Track an event via BOTH Meta Pixel (browser) and CAPI (server-side).
 * Both events get the same event_id so Meta can dedupe them.
 * If iOS14+/Adblocker blocks the Pixel, CAPI still delivers.
 *
 * @param eventName - Standard ('Lead', 'Purchase'...) or custom event name
 * @param userData - PII for matching (raw — gets hashed server-side)
 * @param customData - Event-specific properties (value, content_name, etc.)
 * @param eventSourceUrl - Page URL the event was triggered on
 * @param eventId - Optional shared event_id. Pass this to dedupe against a
 *   server-side CAPI event that already fired with the same id (e.g. the
 *   Schnuppertag booking event). Omit to generate a fresh one.
 * @param opts.ensureFbp - Mint an `_fbp` browser id if the Pixel never set one.
 *   Cold ad traffic rarely grants marketing consent, so the Pixel — and with it
 *   the cookie — never loads; without any id the server event reaches Meta but
 *   cannot be tied to the ad that paid for the click. Only pass this on a
 *   user-initiated step (booking, application, starting a form), never on a
 *   passive page view: that would mint a tracking id without an action to
 *   justify it. Same rationale as captureFbclid in facebookTracking.ts.
 */
export const trackEventWithCAPI = async (
  eventName: string,
  userData?: CAPIUserData,
  customData?: Record<string, unknown>,
  eventSourceUrl?: string,
  eventId?: string,
  opts?: { ensureFbp?: boolean }
): Promise<void> => {
  const resolvedEventId = eventId ?? generateEventId();

  // Erst wenn die Seite sichtbar ist (s. Kopfkommentar); event_time entsteht
  // dadurch ebenfalls erst nach der Aktivierung.
  await bisAktiviert();

  // 1) Fire Pixel-event with eventID (browser-side)
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    const isStandardEvent = ["Lead","Purchase","Subscribe","CompleteRegistration",
      "AddToCart","InitiateCheckout","Contact","ViewContent","PageView","Search"].includes(eventName);
    const trackType = isStandardEvent ? "track" : "trackCustom";
    window.fbq(trackType, eventName, customData || {}, { eventID: resolvedEventId });
  }

  // 2) Fire CAPI-event server-side (best-effort — don't block UX if it fails)
  try {
    // Attach Facebook click/browser ids (_fbc/_fbp) so Meta can attribute this
    // server event to the ad that drove the click. Caller-supplied userData
    // wins on any key conflict (it never sets fbc/fbp, so both are preserved).
    const { marketing_consent, ...fbTracking } = getFacebookTrackingData(
      opts?.ensureFbp ? { ensureFbp: true } : undefined
    );
    // Ohne Marketing-Einwilligung geht nichts an Meta (KA-22, 02.10.2026). Der
    // Server prüft das zusätzlich selbst; hier sparen wir uns den Aufruf.
    if (!marketing_consent) return;
    await supabase.functions.invoke("meta-capi-event", {
      body: {
        marketing_consent,
        event_name: eventName,
        event_id: resolvedEventId,
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: eventSourceUrl || (typeof window !== "undefined" ? window.location.href : undefined),
        user_data: { ...fbTracking, ...(userData || {}) },
        custom_data: customData || {},
      },
    });
  } catch (err) {
    // CAPI failure is not user-facing — Pixel still has the event
    console.warn("[CAPI] event failed, Pixel still fired:", err);
  }
};

/**
 * Nur das Browser-Pixel, mit fester event_id, für ein Event, das der Server bereits per CAPI
 * sendet (HuTa-Warteliste: daycare-lead-intake schickt `HutaWarteliste` mit derselben id, Meta
 * dedupliziert). Kein zweiter CAPI-Aufruf aus dem Browser. Ohne Marketing-Einwilligung nichts.
 */
export const trackPixelMitEventId = async (
  eventName: string,
  customData: Record<string, unknown>,
  eventId: string,
): Promise<void> => {
  await bisAktiviert();
  if (!getFacebookTrackingData().marketing_consent) return;
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  const isStandardEvent = ["Lead","Purchase","Subscribe","CompleteRegistration",
    "AddToCart","InitiateCheckout","Contact","ViewContent","PageView","Search"].includes(eventName);
  window.fbq(isStandardEvent ? "track" : "trackCustom", eventName, customData, { eventID: eventId });
};

/**
 * Execute a callback once window.fbq is available.
 * Retries every 500ms for up to 10 seconds, then gives up.
 * Returns a cleanup function suitable for useEffect.
 */
export const waitForFbq = (callback: () => void): (() => void) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    callback();
    return () => {};
  }

  const interval = setInterval(() => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      clearInterval(interval);
      callback();
    }
  }, 500);

  const timeout = setTimeout(() => clearInterval(interval), 10000);

  return () => {
    clearInterval(interval);
    clearTimeout(timeout);
  };
};
